import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import debounce from "lodash/debounce";
import { Select, Spin } from "antd";
import type { SelectProps } from "antd";

import { RootState } from "Redux/store";
import { fetchCompanies } from "Redux/Admin/Companies/action";

export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, "options" | "children"> {
  /** optional debouncing time for search input */
  debounceTimeout?: number;
}

/**
 * A debounced user-select component that fetches user data 
 * from the server whenever the user types in the search box.
 */
function CompanySelect<
  ValueType extends {
    key?: string;
    label: React.ReactNode;
    value: string | number;
  } = any
>({
  debounceTimeout = 800,
  ...props
}: DebounceSelectProps<ValueType>) {
  const dispatch = useDispatch();
  const { entities, ids, loading } = useSelector((state: RootState) => state.fetchCompaniesReducer);

  // Dispatch an action on first render to load initial data
  useEffect(() => {
    dispatch(fetchCompanies({ search: "", offset: 0, limit: 50 }, () => {}));
  }, [dispatch]);

  // Create a debounced version of the search handler
  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      dispatch(fetchCompanies({ search: value, offset: 0, limit: 50 }, () => {}));
    };
    return debounce(loadOptions, debounceTimeout);
  }, [debounceTimeout, dispatch]);

  // Transform your store's user data into AntD <Select> options
  const data = ids.map((id) => {
    const item = entities[id];

    return {
      label: item.name,
      value: item.id,
    };
  });

  // Transform props.value (ID) to the expected {label, value} format for labelInValue
  const transformedValue = useMemo(() => {
    if (!props.value) return undefined;
    
    // If props.value is already an object with label and value, return as is
    if (typeof props.value === 'object' && !Array.isArray(props.value) && 'label' in props.value && 'value' in props.value) {
      return props.value;
    }
    
    // If props.value is just an ID (string or number), find the corresponding entity and create the object
    if (typeof props.value === 'string' || typeof props.value === 'number') {
      const entity = entities[props.value];
      if (entity) {
        return {
          label: entity.name,
          value: entity.id,
        } as ValueType;
      }
    }
    
    return undefined;
  }, [props.value, entities]);

  return (
    <Select
      // Spread any "leftover" props you might want to pass
      // (e.g., value, onChange, mode, etc.)
      {...props}
      showSearch
      loading={loading}
      options={data}
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={loading ? <Spin size="small" /> : null}
      labelInValue
      value={transformedValue}
    />
  );
}

export default CompanySelect;
